// The share card (spec v2 section 10): a 1080 × 1920 Instagram-story image drawn on a canvas in the browser.
// The lockup is set in type, "MedSnapp Rounds" in the display face like the app's Wordmark (user, 9 Oct 2026: the supplied
// logo read too faint; the cleaned files stay for the favicons and the link preview). The Pulse is drawn here from the
// brand tokens. The card follows the battle's look: Bold, Clinical and Playful draw the light card in their own token values;
// Theatre draws the navy and lime card (THEATRE_PALETTE, drawTheatreCard). The card keeps its key content between
// y = 220 and y = 1700 so Instagram's story chrome never covers it. 9 Oct: no web address on the card (it changes with
// the custom domain, and a story's link sticker does the job); the call to action is a dare built from the result.
import type { Look } from '@shared/types';
import { ordinal } from '@/lib/kudos';

export const CARD_W = 1080;
export const CARD_H = 1920;

export interface ShareFacts {
  title: string; name: string; rank?: number; players: number; score: number;
  accuracy?: number; bestStreak: number; line?: string;
}
export interface CardModel {
  title: string; lead: string; rank: string; rankSuffix: string; of: string;
  stats: Array<{ value: string; label: string }>; line?: string; cta: string;
}

const num = (v: number) => v.toLocaleString('en-GB');
/** "57th", "1,203rd": an ordinal place as people write it. */
const placeOrdinal = (rank: number) => `${num(rank)}${ordinal(rank).replace(/^\d+/, '')}`;
/**
 * The call to action at the foot of every card (product owner, 9 Oct 2026), a dare built from the result: "Think you can
 * beat 5th?", "Think you can beat 1st?"; with no place to dare (no question closed with an answer in), "this".
 */
export const CARD_CTA = {
  dare: (rank: number) => `Think you can beat ${placeOrdinal(rank)}?`,
  none: 'Think you can beat this?',
} as const;
/** The card's call to action for a result: the dare at its place, or at "this" without one. */
export const cardCta = (rank?: number): string => (rank ? CARD_CTA.dare(rank) : CARD_CTA.none);

/** "57th of 212": the place as people say it (alt text, screen readers, the share text). */
export const placeText = (rank: number, players: number) => `${placeOrdinal(rank)} of ${num(Math.max(players, rank))}`;

/**
 * Everything the card says, worked out from the result. Pure, so it is tested without a canvas. Each fact once: the
 * wordmark is the brand, so a battle without a title has no title line (never "MedSnapp Rounds" twice), and a figure
 * with nothing in it (no accuracy, a best streak of 0) is left off rather than drawn as a dash or a zero.
 */
export function cardModel(f: ShareFacts): CardModel {
  const accuracy = f.accuracy !== undefined ? [{ value: `${f.accuracy}%`, label: 'accuracy' }] : [];
  const streak = f.bestStreak > 0 ? [{ value: String(f.bestStreak), label: 'best streak' }] : [];
  const shared = { title: f.title.trim(), line: f.line, cta: cardCta(f.rank) };
  if (f.rank) {
    return {
      ...shared, lead: `${f.name} finished`, rank: num(f.rank), rankSuffix: ordinal(f.rank).replace(/^\d+/, ''),
      of: `of ${num(Math.max(f.players, f.rank))}`, stats: [{ value: num(f.score), label: 'points' }, ...accuracy, ...streak],
    };
  }
  // No place to show (the battle ended before a question closed): the score is the headline, never a giant dash.
  return {
    ...shared, lead: `${f.name} scored`, rank: num(f.score), rankSuffix: '', of: 'points',
    stats: [...accuracy, ...streak, { value: num(f.players), label: f.players === 1 ? 'player' : 'players' }],
  };
}

export interface Palette { bg: string; surface: string; surface2: string; line: string; ink: string; ink2: string; ink3: string; brand: string; brandStrong: string; brandTint: string; lime: string; cart: string; iv: string }
const FALLBACK: Palette = { bg: '#F4F7F6', surface: '#FFFFFF', surface2: '#EAF0EE', line: '#D9E2DF', ink: '#0D1B2A', ink2: '#34445A', ink3: '#56677B', brand: '#0E9F94', brandStrong: '#087F76', brandTint: '#ECF9F7', lime: '#C2F23D', cart: '#FF5B22', iv: '#2F5BFF' };
const VARS: Record<keyof Palette, string> = { bg: '--bg', surface: '--surface', surface2: '--surface-2', line: '--line', ink: '--ink', ink2: '--ink-2', ink3: '--ink-3', brand: '--brand', brandStrong: '--brand-strong', brandTint: '--brand-tint', lime: '--lime', cart: '--cart', iv: '--iv' };

/**
 * The theatre card's colours, fixed values from the Rounds palette (the same as the --th-* helpers in styles/looks/theatre.css):
 * night-shift navy ground, ward white words, Bold's brand-soft and line for the quieter words, Bold's ink-2 for rules on navy,
 * monitor lime for the place, the Pulse and the call to action. The rest are Bold's values (the theatre card does not use them).
 */
export const THEATRE_PALETTE: Readonly<Palette> = {
  bg: '#0D1B2A', surface: '#FFFFFF', surface2: '#C4ECE5', line: '#2E3F55', ink: '#F4F7F6', ink2: '#B6F0E8', ink3: '#9CDDD3',
  brand: '#00B5A5', brandStrong: '#006E64', brandTint: '#D9F6F2', lime: '#E0FF3A', cart: '#FF4F00', iv: '#0E5AFF',
};

/** The live token values, so the card follows the theme; the spec's values when a token is missing. */
export function readPalette(): Palette {
  const out = { ...FALLBACK };
  try {
    const cs = getComputedStyle(document.documentElement);
    for (const k of Object.keys(VARS) as Array<keyof Palette>) { const v = cs.getPropertyValue(VARS[k]).trim(); if (/^#|^rgb|^hsl|^oklch/i.test(v)) out[k] = v; }
  } catch { /* fall back to the spec values */ }
  return out;
}

/** The card's colours for a look: Theatre's own palette, otherwise the live tokens of the look on the page. */
export function cardPalette(look?: Look): Palette {
  return look === 'theatre' ? { ...THEATRE_PALETTE } : readPalette();
}

// The display face for the wordmark, the title and the numbers; the interface face for everything else, the line too
// (the handwriting face is retired from the clinical look).
export const FONTS = {
  display: (px: number, w = 800) => `${w} ${px}px "Bricolage Grotesque", "Instrument Sans", system-ui, sans-serif`,
  ui: (px: number, w = 600) => `${w} ${px}px "Instrument Sans", system-ui, -apple-system, sans-serif`,
};

/** Breaks text into lines no wider than `max`, at most `maxLines` (the last line ends with an ellipsis if cut). */
export function wrapLines(measure: (s: string) => number, text: string, max: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean); const lines: string[] = []; let cur = '';
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (measure(next) <= max || !cur) cur = next; else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines); let last = kept[maxLines - 1];
  while (last.length > 1 && measure(`${last}…`) > max) last = last.slice(0, -1).trimEnd();
  kept[maxLines - 1] = `${last}…`;
  return kept;
}

type Ctx = CanvasRenderingContext2D;

/** The largest size from `start` down to `min` (in steps of 2 px) at which `text` fits in `max`; leaves ctx.font set to it. */
export function fitFont(ctx: Ctx, text: string, max: number, start: number, min: number, font: (px: number) => string): number {
  let size = start; ctx.font = font(size);
  while (size > min && ctx.measureText(text).width > max) { size -= 2; ctx.font = font(size); }
  return size;
}

/** The Pulse as one stroke across the card: steady beats, then the big celebratory spike under the rank. No glow when glowAlpha is 0. */
function drawPulse(ctx: Ctx, y: number, colour: string, glow: string, glowAlpha = 0.55) {
  const beat = (x: number, h: number) => { ctx.lineTo(x, y); ctx.lineTo(x + 14, y - h * 0.18); ctx.lineTo(x + 26, y + h * 0.22); ctx.lineTo(x + 42, y - h); ctx.lineTo(x + 58, y + h * 0.42); ctx.lineTo(x + 70, y - h * 0.12); ctx.lineTo(x + 84, y); };
  const path = () => {
    ctx.beginPath(); ctx.moveTo(0, y);
    beat(110, 44); beat(330, 50); beat(470, 150); beat(700, 52); ctx.lineTo(CARD_W, y);
  };
  ctx.save(); ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  if (glowAlpha > 0) { ctx.globalAlpha = glowAlpha; ctx.strokeStyle = glow; ctx.lineWidth = 26; path(); ctx.stroke(); }
  ctx.globalAlpha = 1; ctx.strokeStyle = colour; ctx.lineWidth = 9; path(); ctx.stroke();
  ctx.restore();
}

/** Where the lockup sits: its left edge, its baseline and the size of both words. */
export const LOCKUP = { x: 96, y: 300, size: 70 } as const;
/**
 * The lockup at the top, set in type like the app's Wordmark: "MedSnapp" semibold and "Rounds" extrabold, both at full size in
 * the card's ink (MedSnapp is never small or muted beside Rounds).
 */
function drawLockup(ctx: Ctx, ink: string) {
  const { x, y, size } = LOCKUP;
  ctx.fillStyle = ink; ctx.font = FONTS.display(size, 600); ctx.fillText('MedSnapp', x, y);
  const roundsX = x + ctx.measureText('MedSnapp').width + Math.round(size * 0.28);
  ctx.font = FONTS.display(size, 800); ctx.fillText('Rounds', roundsX, y);
}

/** A filled rectangle with round corners (drawn with arcTo, which every canvas has). */
function fillRounded(ctx: Ctx, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath(); ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath(); ctx.fill();
}

/** Draws the whole card for the battle's look. Every text it draws comes from the model. */
export function drawCard(ctx: Ctx, m: CardModel, p: Palette, look?: Look) {
  if (look === 'theatre') { drawTheatreCard(ctx, m, p); return; }
  const W = CARD_W; const H = CARD_H; const pad = 96;
  ctx.save();
  ctx.fillStyle = p.bg; ctx.fillRect(0, 0, W, H);
  // A faint dotted grid, like a chart pinned to the ward wall.
  ctx.fillStyle = p.line; ctx.globalAlpha = 0.7;
  for (let y = 24; y < H; y += 40) for (let x = 24; x < W; x += 40) { ctx.beginPath(); ctx.arc(x, y, 2.2, 0, Math.PI * 2); ctx.fill(); }
  ctx.globalAlpha = 1;
  ctx.textBaseline = 'alphabetic'; ctx.textAlign = 'left';

  // The lockup, in type.
  drawLockup(ctx, p.ink);

  // Battle title, when it has one.
  if (m.title) {
    ctx.fillStyle = p.ink2; ctx.font = FONTS.display(58, 700);
    const titleLines = wrapLines((s) => ctx.measureText(s).width, m.title, W - pad * 2, 2);
    titleLines.forEach((l, i) => ctx.fillText(l, pad, 470 + i * 70));
  }

  // Who, and the rank in giant numerals.
  ctx.fillStyle = p.ink3; ctx.font = FONTS.ui(46, 600); ctx.fillText(m.lead, pad, 690);
  ctx.fillStyle = p.ink; let size = 400; ctx.font = FONTS.display(size);
  while (size > 160 && ctx.measureText(m.rank).width > W - pad * 2 - 170) { size -= 20; ctx.font = FONTS.display(size); }
  ctx.fillText(m.rank, pad - 12, 1040);
  const rankW = ctx.measureText(m.rank).width;
  if (m.rankSuffix) { ctx.fillStyle = p.brandStrong; ctx.font = FONTS.display(Math.round(size * 0.32)); ctx.fillText(m.rankSuffix, pad - 12 + rankW + 10, 1040 - size * 0.45); }
  ctx.fillStyle = p.ink3; ctx.font = FONTS.display(76, 700); ctx.fillText(m.of, pad, 1140);

  // The Pulse sweeps across, spiking under the rank.
  drawPulse(ctx, 1240, p.brand, p.lime);

  // Three figures.
  const colW = (W - pad * 2) / 3;
  m.stats.forEach((s, i) => {
    const x = pad + i * colW;
    if (i > 0) { ctx.fillStyle = p.line; ctx.fillRect(x - 1, 1330, 2, 130); }
    ctx.fillStyle = p.ink; ctx.font = FONTS.display(92); ctx.fillText(s.value, x + (i ? 28 : 0), 1420);
    ctx.fillStyle = p.ink3; ctx.font = FONTS.ui(34, 600); ctx.fillText(s.label, x + (i ? 28 : 0), 1468);
  });

  // The line, in the interface face, on up to two lines.
  if (m.line) {
    ctx.fillStyle = p.brandStrong; ctx.font = FONTS.ui(44, 700);
    wrapLines((s) => ctx.measureText(s).width, m.line, W - pad * 2, 2).forEach((l, i) => ctx.fillText(l, pad, 1548 + i * 54));
  }

  // The dare, where the invitation always stood; no address beneath it.
  ctx.fillStyle = p.ink; fitFont(ctx, m.cta, W - pad * 2, 60, 40, (px) => FONTS.display(px)); ctx.fillText(m.cta, pad, 1682);
  ctx.restore();
}

/** Where the theatre card's call to action sits: a lime block across the foot of the safe area. */
export const THEATRE_CTA_BOX = { x: 96, y: 1596, w: CARD_W - 192, h: 156, r: 36 } as const;
/**
 * The theatre card (9 Oct 2026): night-shift navy, no grid, the place in lime at about half the card's width, the Pulse as one
 * trace, three figures, the line, and a lime block with the dare in navy.
 */
function drawTheatreCard(ctx: Ctx, m: CardModel, p: Palette) {
  const W = CARD_W; const H = CARD_H; const pad = 96;
  ctx.save();
  ctx.fillStyle = p.bg; ctx.fillRect(0, 0, W, H);
  ctx.textBaseline = 'alphabetic'; ctx.textAlign = 'left';

  drawLockup(ctx, p.ink);

  if (m.title) {
    ctx.fillStyle = p.ink2; ctx.font = FONTS.ui(48, 600);
    wrapLines((s) => ctx.measureText(s).width, m.title, W - pad * 2, 2).forEach((l, i) => ctx.fillText(l, pad, 420 + i * 60));
  }

  // Who, then the place: lime, as big as it fits (about half the card's width for a one- or two-digit place).
  ctx.fillStyle = p.ink3; ctx.font = FONTS.ui(46, 600); ctx.fillText(m.lead, pad, 610);
  let size = 520; ctx.font = FONTS.display(size);
  while (size > 200 && ctx.measureText(m.rank).width > W - pad * 2 - 190) { size -= 20; ctx.font = FONTS.display(size); }
  ctx.fillStyle = p.lime; ctx.fillText(m.rank, pad - 14, 1030);
  const rankW = ctx.measureText(m.rank).width;
  if (m.rankSuffix) { ctx.fillStyle = p.ink; ctx.font = FONTS.display(Math.round(size * 0.3)); ctx.fillText(m.rankSuffix, pad - 14 + rankW + 12, 1030 - size * 0.48); }
  ctx.fillStyle = p.ink2; ctx.font = FONTS.ui(54, 600); ctx.fillText(m.of, pad, 1120);

  // The Pulse, one lime trace, no glow.
  drawPulse(ctx, 1225, p.lime, p.lime, 0);

  // Three figures, no rules between them.
  const colW = (W - pad * 2) / 3;
  m.stats.forEach((s, i) => {
    const x = pad + i * colW;
    ctx.fillStyle = p.ink; ctx.font = FONTS.display(96); ctx.fillText(s.value, x, 1390);
    ctx.fillStyle = p.ink3; ctx.font = FONTS.ui(36, 600); ctx.fillText(s.label, x, 1436);
  });

  if (m.line) {
    ctx.fillStyle = p.ink2; ctx.font = FONTS.ui(44, 600);
    wrapLines((s) => ctx.measureText(s).width, m.line, W - pad * 2, 2).forEach((l, i) => ctx.fillText(l, pad, 1508 + i * 54));
  }

  // The call to action: a lime block, the dare in navy on one line, centred in it (no address beneath).
  const b = THEATRE_CTA_BOX; const inner = 44;
  ctx.fillStyle = p.lime; fillRounded(ctx, b.x, b.y, b.w, b.h, b.r);
  ctx.fillStyle = p.bg; const ctaSize = fitFont(ctx, m.cta, b.w - inner * 2, 62, 40, (px) => FONTS.display(px));
  ctx.fillText(m.cta, b.x + inner, b.y + Math.round(b.h / 2 + ctaSize * 0.35));
  ctx.restore();
}

/** Waits (briefly) for the brand faces so the card is drawn in them; draws in the fallbacks if they are slow. */
async function fontsReady(timeoutMs = 1500) {
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts?.load) return;
  const loads = Promise.all([fonts.load(FONTS.display(100)), fonts.load(FONTS.display(100, 700)), fonts.load(FONTS.ui(40)), fonts.load(FONTS.ui(40, 700))]).catch(() => undefined);
  await Promise.race([loads, new Promise((r) => setTimeout(r, timeoutMs))]);
}

/** Renders the card to a PNG in the battle's look. Undefined when this browser cannot draw it. */
export async function renderCard(m: CardModel, look?: Look, palette: Palette = cardPalette(look)): Promise<Blob | undefined> {
  try {
    await fontsReady();
    const canvas = document.createElement('canvas'); canvas.width = CARD_W; canvas.height = CARD_H;
    const ctx = canvas.getContext('2d'); if (!ctx) return undefined;
    drawCard(ctx, m, palette, look);
    return await new Promise<Blob | undefined>((resolve) => canvas.toBlob((b) => resolve(b ?? undefined), 'image/png'));
  } catch { return undefined; }
}
